/**
 * Helpers for OncoEMR user pickers (assignee, message recipients).
 * @module oncoemrUsersHelpers
 */

import {
  OncoemrUser,
  OncoemrUserSelectOption,
} from "../data-model/oncoemrUsersModel";

/** Value used when a task has no assignee selected. */
export const ONCOEMR_UNASSIGNED_ASSIGNEE_VALUE = "";

/** Label shown for the unassigned assignee option. */
export const ONCOEMR_UNASSIGNED_ASSIGNEE_LABEL = "Unassigned";

/**
 * Maps API users to {@link OncoemrUserSelectOption} rows for SearchableSelect.
 *
 * @param users - Enabled users for the current org.
 * @returns Options sorted by label.
 */
export const mapOncoemrUsersToSelectOptions = (
  users: OncoemrUser[],
): OncoemrUserSelectOption[] =>
  users
    .map((user) => ({
      label: user.first_and_last,
      value: user.oncoemr_user_id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Assignee dropdown options: unassigned plus all org users.
 *
 * @param users - Enabled users for the current org.
 * @returns Options for the create-task assignee field.
 */
export const buildAssigneeSelectOptions = (
  users: OncoemrUser[],
): OncoemrUserSelectOption[] => [
  {
    label: ONCOEMR_UNASSIGNED_ASSIGNEE_LABEL,
    value: ONCOEMR_UNASSIGNED_ASSIGNEE_VALUE,
  },
  ...mapOncoemrUsersToSelectOptions(users),
];

/**
 * Case-insensitive substring filter on `first_and_last`.
 *
 * @param users - Full org roster from Redux cache.
 * @param query - User-typed search text; empty returns all users.
 * @returns Matching users in display-name order.
 */
export const filterOncoemrUsersByQuery = (
  users: OncoemrUser[],
  query: string,
): OncoemrUser[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return users;
  }

  return users.filter((user) =>
    user.first_and_last.toLowerCase().includes(normalized),
  );
};

/**
 * Resolves a user's display name from the cached org roster.
 *
 * @param users - Cached users for the org.
 * @param oncoemrUserId - User id to look up.
 * @returns `first_and_last` or the id when not found.
 */
export const getOncoemrUserDisplayName = (
  users: OncoemrUser[],
  oncoemrUserId: string,
): string =>
  users.find((user) => user.oncoemr_user_id === oncoemrUserId)
    ?.first_and_last ?? oncoemrUserId;
